import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Btn, Loading, Msg, Section, fmtDate, panelGet, panelPost, useAdminGet } from '../kit';
import { VaryingNotices } from './VaryingNotice';
import DefaultFallbacks from './DefaultFallbacks';
import { FallbacksPanel, fallbackNames } from './content/ContentEditor';
import {
  Badge, Cta, EmailPreview, Labeled, Modal, Note, Placeholder, fmtFrom, inputCls,
  placeholdersIn, renderDraft, toCtas, toPlaceholders,
} from './emailKit';

/**
 * Templates (editable) — every customer-facing e-mail the platform sends, one
 * editor for all of them (backend: email_templates).
 *
 * The copy used to be split: five keys were editable (subject + body only) and
 * the other twenty-odd lived inline in db_ops.py, so changing a trial e-mail's
 * wording, its sender or its button meant a deploy. The store now holds every
 * field a send uses — subject, body, From, Reply-To and up to three buttons —
 * and a saved edit takes effect on the next send. Reset deletes the edit and
 * the code default comes back, so the repo's copy stays the fallback. A field
 * left blank follows the built-in value (the server stores it that way too).
 *
 * The preview is the server's own render — of the unsaved draft while you
 * type (render_template takes the draft fields), so what you see is exactly
 * what would go out, buttons and plain-text part included. Only when a draft
 * is too long to travel in a preview request, or the request fails, does the
 * panel fall back to an in-browser approximation, and it says so.
 * "Send test to me" mails the calling admin only — the editor's contents,
 * saved or not — and nobody else.
 *
 * Fallbacks (owner, 2026-10-27): each template may say what a placeholder
 * becomes for someone with no value ({first_name} -> "member" …), over the
 * global Default fallbacks shown above the list; saved with the template's
 * other fields (placeholder_fallbacks).
 */

type Draft = {
  subject: string; body: string; from_name: string; from_email: string; reply_to: string; ctas: Cta[];
  fallbacks: Record<string, string>;
};
type Tpl = {
  key: string; name: string; group: string; description: string; trigger: string; fn: string;
  placeholders: Placeholder[]; edited: boolean; editedFields: string[]; orphan: boolean; sendHtml: boolean;
  updated_by: string; updated_at: string | null;
  current: Draft; defaults: Draft;
};

const DEFAULT_MAX_CTAS = 3;
const DEFAULT_DOMAIN = '@universitytech.io';
// Past this the draft no longer fits in a preview URL (proxies refuse long
// request lines); the panel previews it in the browser instead.
const MAX_DRAFT_QUERY = 7000;

const draftOf = (s: any): Draft => ({
  subject: String(s?.subject ?? ''),
  body: String(s?.body ?? ''),
  from_name: String(s?.from_name ?? ''),
  from_email: String(s?.from_email ?? ''),
  reply_to: String(s?.reply_to ?? ''),
  ctas: toCtas(s?.ctas),
  fallbacks: Object.fromEntries(Object.entries(
    s?.placeholder_fallbacks && typeof s.placeholder_fallbacks === 'object' ? s.placeholder_fallbacks : {})
    .map(([k, v]) => [k, String(v ?? '')]).sort(([a], [b]) => a.localeCompare(b))),
});

/** admin_list sends the defaults both nested ({defaults}) and flat
 *  (default_subject, …); either reads the same here. */
const normalize = (t: any): Tpl => {
  const dflt = t?.defaults || {
    subject: t?.default_subject, body: t?.default_body, from_name: t?.default_from_name,
    from_email: t?.default_from_email, reply_to: t?.default_reply_to, ctas: t?.default_ctas,
  };
  return {
    key: String(t?.key ?? ''),
    name: String(t?.name || t?.key || ''),
    group: String(t?.group ?? ''),
    description: String(t?.description ?? ''),
    trigger: String(t?.trigger ?? ''),
    fn: String(t?.function ?? ''),
    placeholders: toPlaceholders(t?.placeholders),
    edited: !!t?.edited,
    editedFields: Array.isArray(t?.edited_fields) ? t.edited_fields.map(String) : [],
    orphan: !!t?.orphan,
    sendHtml: t?.send_html !== false,
    updated_by: String(t?.updated_by ?? ''),
    updated_at: t?.updated_at ?? null,
    current: draftOf(t),
    defaults: { ...draftOf(dflt), fallbacks: {} },
  };
};

/** A draft with its fallbacks in a fixed order and blanks dropped, so comparing two is order-proof. */
const norm = (d: Draft): Draft => ({
  ...d,
  fallbacks: Object.fromEntries(Object.entries(d.fallbacks).filter(([, v]) => v.trim())
    .sort(([x], [y]) => x.localeCompare(y))),
});
const same = (a: Draft, b: Draft) => JSON.stringify(norm(a)) === JSON.stringify(norm(b));
const FIELD_LABEL: Record<keyof Draft, string> = {
  subject: 'Subject', body: 'Body', from_name: 'From name', from_email: 'From email',
  reply_to: 'Reply-To', ctas: 'Buttons', fallbacks: 'Fallbacks',
};
const EMAIL_RE = /^[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
/** "Name <a@b.c>" or "a@b.c" -> the address. */
const addressOf = (s: string) => {
  const m = s.match(/<([^>]+)>\s*$/);
  return (m ? m[1] : s).trim();
};

/**
 * Mirror of email_templates._check_placeholders: the server fills text with
 * str.format_map and, on a format error, mails the raw text (every {name}
 * still in braces, a {upgrade_url} button dropped). So a lone { or }, or
 * anything but a plain {name} ({0}, {}, {a.b}, {a!r}, {a:,}), is an error.
 * {{ and }} are literal braces.
 */
const braceProblem = (where: string, text: string): string | null => {
  const s = text || '';
  let i = 0;
  while (i < s.length) {
    const ch = s[i];
    if (ch === '{') {
      if (s[i + 1] === '{') { i += 2; continue; }
      let depth = 1;
      let j = i + 1;
      while (j < s.length && depth) {
        if (s[j] === '{') depth++;
        else if (s[j] === '}') depth--;
        j++;
      }
      if (depth) return `${where}: a { has no closing }. For a literal brace type {{.`;
      const field = s.slice(i + 1, j - 1);
      if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(field)) {
        return `${where}: {${field}} is not a placeholder. Use a plain name such as {first_name}`
          + ' (no dots, brackets, numbers, ! or :); for a literal brace type {{ or }}.';
      }
      i = j;
      continue;
    }
    if (ch === '}') {
      if (s[i + 1] === '}') { i += 2; continue; }
      return `${where}: a } has no opening {. For a literal brace type }}.`;
    }
    i++;
  }
  return null;
};

/**
 * The server's own rules (email_templates._validate), checked as you type so
 * Save is only offered for an edit the server will accept; plus warnings for
 * things that save fine but probably are not what was meant.
 */
const check = (d: Draft, known: string[], maxCtas: number, domain: string) => {
  const errors: string[] = [];
  const warnings: string[] = [];
  if (!d.body.trim()) errors.push('The body cannot be empty — use Reset to go back to the default.');
  if (d.subject.trim().length > 250) errors.push('The subject is too long (250 characters at most).');
  if (d.from_name.trim().length > 100) errors.push('From name is too long (100 characters at most).');
  const fe = d.from_email.trim().toLowerCase();
  if (fe && !(EMAIL_RE.test(fe) && fe.endsWith(domain))) {
    errors.push(`From email must be an address ending in ${domain} (the verified sending domain).`);
  }
  if (d.reply_to.trim() && !EMAIL_RE.test(addressOf(d.reply_to.trim()))) {
    errors.push('Reply-To must be an e-mail address, or Name <address>.');
  }
  const ctas = d.ctas.filter(c => c.label.trim() || c.url.trim());
  if (ctas.length > maxCtas) errors.push(`At most ${maxCtas} buttons.`);
  ctas.forEach((c, i) => {
    const n = c.label.trim() ? `Button “${c.label.trim()}”` : `Button ${i + 1}`;
    const url = c.url.trim();
    if (!c.label.trim()) errors.push(`${n} needs a label.`);
    else if (c.label.trim().length > 60) errors.push(`${n}: the label is too long (60 characters at most).`);
    if (!url) errors.push(`${n} needs a link.`);
    else if (!/^(https?:\/\/|mailto:|\{)/i.test(url)) {
      errors.push(`${n}: the link must start with https://, http://, mailto: or a {placeholder}.`);
    } else if (/\s/.test(url)) errors.push(`${n}: the link cannot contain spaces.`);
    [braceProblem(`${n} label`, c.label.trim()), braceProblem(`${n} link`, url)]
      .forEach(p => { if (p) errors.push(p); });
  });
  [braceProblem('Subject', d.subject.trim()), braceProblem('Body', d.body),
    braceProblem('Reply-To', d.reply_to.trim())].forEach(p => { if (p) errors.push(p); });
  const unknown = placeholdersIn(d.subject, d.body, ...ctas.map(c => c.label + ' ' + c.url))
    .filter(p => !known.includes(p));
  if (unknown.length) {
    warnings.push(`Not a placeholder this e-mail knows, so it will send blank: ${unknown.map(u => `{${u}}`).join(', ')}.`);
  }
  return { errors, warnings };
};

const wireDraft = (d: Draft): Record<string, string> => ({
  subject: d.subject, body: d.body, from_name: d.from_name.trim(), from_email: d.from_email.trim(),
  reply_to: d.reply_to.trim(),
  ctas: JSON.stringify(d.ctas.filter(c => c.label.trim() || c.url.trim())
    .map(c => ({ label: c.label.trim(), url: c.url.trim() }))),
  placeholder_fallbacks: JSON.stringify(Object.fromEntries(
    Object.entries(d.fallbacks).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v))),
});

// ---------------------------------------------------------------------------
/**
 * Embedding elsewhere (e.g. Admin → IP Intel Report → Emails): `groups` and/or
 * `keyPrefix` narrow the editor to those templates — a template is kept when
 * its group is listed or its key starts with the prefix — and `title` replaces
 * the section heading. With none of them (Email Center) every template shows,
 * exactly as before.
 */
type EditableTemplatesProps = {
  groups?: string[]; keyPrefix?: string; title?: string;
  /** Open this template (a deep link: ?tab=templates&template=<key>). */
  initialKey?: string;
};

const EditableTemplates: React.FC<EditableTemplatesProps> = ({ groups: onlyGroups, keyPrefix, title, initialKey }) => {
  const res = useAdminGet('email', 'editable_templates');
  const payload: any = res.data || {};
  const narrowed = !!(onlyGroups?.length || keyPrefix);
  const list: Tpl[] = useMemo(() => {
    // Saved templates (lib_…, library: true) come in this list too, for the
    // pickers that read it; they are edited under Saved templates, not here.
    const all = ((payload.templates || (Array.isArray(res.data) ? res.data : [])) as any[])
      .filter(t => !t?.library).map(normalize);
    if (!narrowed) return all;
    return all.filter(t => (onlyGroups || []).includes(t.group) || (!!keyPrefix && t.key.startsWith(keyPrefix)));
  }, [res.data, narrowed, (onlyGroups || []).join('|'), keyPrefix]); // eslint-disable-line react-hooks/exhaustive-deps
  const siteValues: Record<string, any> = payload.site_values || {};
  const maxCtas = Number(payload.limits?.max_ctas || DEFAULT_MAX_CTAS);
  const domain = String(payload.limits?.from_domain || DEFAULT_DOMAIN);

  const [sel, setSel] = useState('');
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState<Note>(null);
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState<'' | 'reset' | 'test'>('');

  useEffect(() => { if (!sel && list.length) setSel(list[0].key); }, [list, sel]);
  // A deep link picks its template once the list is in.
  useEffect(() => {
    if (initialKey && list.some(t => t.key === initialKey)) { setSel(initialKey); setMsg(null); }
  }, [initialKey, list.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const tpl = list.find(t => t.key === sel) || null;
  const draft = tpl ? (drafts[tpl.key] || tpl.current) : null;
  const dirty = !!(tpl && drafts[tpl.key] && !same(drafts[tpl.key], tpl.current));

  const setDraft = useCallback((patch: Partial<Draft>) => {
    if (!tpl) return;
    setDrafts(ds => ({ ...ds, [tpl.key]: { ...(ds[tpl.key] || tpl.current), ...patch } }));
  }, [tpl]);
  const dropDraft = (key: string) => setDrafts(ds => { const n = { ...ds }; delete n[key]; return n; });

  const shown = list.filter(t => {
    const n = q.trim().toLowerCase();
    return !n || [t.name, t.key, t.trigger, t.description, t.group, t.current.subject]
      .some(s => s.toLowerCase().includes(n));
  });
  // Groups in the order the server lists them.
  const groups = useMemo(() => {
    const out: { group: string; items: Tpl[] }[] = [];
    shown.forEach(t => {
      const g = t.orphan ? 'No longer used' : (t.group || 'Other');
      let hit = out.find(x => x.group === g);
      if (!hit) { hit = { group: g, items: [] }; out.push(hit); }
      hit.items.push(t);
    });
    return out;
  }, [shown]);
  const nEdited = list.filter(t => t.edited).length;

  // -- preview: the server's render of the saved version or of the draft ----
  const [previewEmail, setPreviewEmail] = useState('');
  const [previewFor, setPreviewFor] = useState('');      // '' = sample values
  const [rendered, setRendered] = useState<any>(null);
  const [renderErr, setRenderErr] = useState('');
  const [renderTick, setRenderTick] = useState(0);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const seq = useRef(0);
  const draftWire = dirty && draft ? wireDraft(draft) : null;
  const draftKey = draftWire ? JSON.stringify(draftWire) : '';
  const draftTooLong = encodeURIComponent(draftKey).length > MAX_DRAFT_QUERY;
  useEffect(() => {
    if (!sel) return undefined;
    const mine = ++seq.current;
    const run = () => {
      setLoadingPreview(true); setRenderErr('');
      const params: Record<string, string> = { key: sel };
      if (previewFor) params.email = previewFor;
      if (draftWire && !draftTooLong) Object.assign(params, draftWire);
      panelGet('email', 'render_template', params).then(({ data, error }) => {
        if (mine !== seq.current) return;
        setLoadingPreview(false);
        if (error) { setRenderErr(error); return; }
        setRendered({ ...(data as any), _key: sel, _for: draftWire && !draftTooLong ? draftKey : '' });
      });
    };
    // Typing re-renders after a short pause, not on every keystroke.
    if (draftWire) { const t = setTimeout(run, 350); return () => clearTimeout(t); }
    run();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel, previewFor, renderTick, draftKey]);

  const shownRender = rendered?._key === sel ? rendered : null;
  const values: Record<string, any> | null = shownRender?.values || null;
  const known = useMemo(() => {
    if (!tpl) return [] as string[];
    const inDefault = placeholdersIn(tpl.defaults.subject, tpl.defaults.body,
      ...tpl.defaults.ctas.map(c => c.label + ' ' + c.url));
    return Array.from(new Set([...tpl.placeholders.map(p => p.name), ...Object.keys(siteValues),
      ...Object.keys(values || {}), ...inDefault]));
  }, [tpl, values, siteValues]);

  // Chips: what the template documents, then the site-wide numbers every
  // template may use, then anything its default copy uses undocumented.
  const chips: Placeholder[] = useMemo(() => {
    if (!tpl) return [];
    const out: Placeholder[] = [...tpl.placeholders];
    const add = (name: string, meaning: string) => { if (!out.some(p => p.name === name)) out.push({ name, meaning }); };
    Object.keys(siteValues).forEach(k => add(k, 'site-wide value, available in every template'));
    placeholdersIn(tpl.defaults.subject, tpl.defaults.body, ...tpl.defaults.ctas.map(c => c.label + ' ' + c.url))
      .forEach(k => add(k, ''));
    return out;
  }, [tpl, siteValues]);

  // The placeholders this draft uses that someone may have no value for.
  const fbNames = useMemo(() => (draft ? fallbackNames(placeholdersIn(draft.subject, draft.body,
    ...draft.ctas.flatMap(c => [c.label, c.url]))).filter(p => !(p in siteValues)) : []),
  [draft, siteValues]);

  // -- placeholder insertion at the caret -----------------------------------
  const lastField = useRef<{ id: string; el: HTMLInputElement | HTMLTextAreaElement } | null>(null);
  const track = (id: string) => (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    lastField.current = { id, el: e.currentTarget };
  };
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const getField = (id: string): string => {
    if (!draft) return '';
    if (id.startsWith('cta_')) {
      const [, part, i] = id.split('_');
      const c = draft.ctas[Number(i)];
      return c ? (part === 'label' ? c.label : c.url) : '';
    }
    return String((draft as any)[id] ?? '');
  };
  const setField = (id: string, v: string) => {
    if (!draft) return;
    if (id.startsWith('cta_')) {
      const [, part, i] = id.split('_');
      setDraft({ ctas: draft.ctas.map((c, j) => j === Number(i) ? { ...c, [part]: v } : c) });
    } else setDraft({ [id]: v } as Partial<Draft>);
  };
  const insert = (name: string) => {
    const token = `{${name}}`;
    const target = lastField.current && document.body.contains(lastField.current.el)
      ? lastField.current : (bodyRef.current ? { id: 'body', el: bodyRef.current } : null);
    if (!target) return;
    const cur = getField(target.id);
    const start = target.el.selectionStart ?? cur.length;
    const end = target.el.selectionEnd ?? cur.length;
    setField(target.id, cur.slice(0, start) + token + cur.slice(end));
    const el = target.el;
    requestAnimationFrame(() => {
      el.focus();
      const at = start + token.length;
      try { el.setSelectionRange(at, at); } catch { /* not a text field */ }
    });
  };

  // -- actions ------------------------------------------------------------
  const issues = draft ? check(draft, known, maxCtas, domain) : { errors: [] as string[], warnings: [] as string[] };

  const save = async () => {
    if (!tpl || !draft) return;
    setBusy('save'); setMsg(null);
    const w = wireDraft(draft);
    const { data, error } = await panelPost('email', 'save_template', {
      key: tpl.key, subject: w.subject, body: w.body, from_name: w.from_name, from_email: w.from_email,
      reply_to: w.reply_to, ctas: JSON.parse(w.ctas), placeholder_fallbacks: JSON.parse(w.placeholder_fallbacks),
    });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({
      kind: 'ok',
      text: (data as any)?.edited === false
        ? `"${tpl.name}" matches the built-in copy in every field, so no edit is stored — it follows the code again.`
        : `Saved "${tpl.name}". The next ${tpl.name} e-mail uses this copy.`,
    });
    dropDraft(tpl.key);
    res.reload();
    setRenderTick(t => t + 1);
  };

  const reset = async () => {
    if (!tpl) return;
    setConfirm(''); setBusy('reset'); setMsg(null);
    const { error } = await panelPost('email', 'reset_template', { key: tpl.key });
    setBusy('');
    if (error) { setMsg({ kind: 'error', text: error }); return; }
    setMsg({ kind: 'ok', text: `"${tpl.name}" is back to the built-in copy.` });
    dropDraft(tpl.key);
    res.reload();
    setRenderTick(t => t + 1);
  };

  const sendTest = async () => {
    if (!tpl || !draft) return;
    setConfirm(''); setBusy('test'); setMsg(null);
    const body: Record<string, any> = { key: tpl.key, confirm: true };
    if (dirty) Object.assign(body, wireDraft(draft));
    const { data, error } = await panelPost('email', 'send_template_test', body);
    setBusy('');
    const d: any = data;
    if (error || d?.sent === false || d?.success === false) {
      setMsg({ kind: 'error', text: error || d?.error || 'The test e-mail was not sent.' });
      return;
    }
    setMsg({ kind: 'ok', text: `Test sent to ${d?.to || 'your admin address'}: “${d?.subject || tpl.name}”.` });
  };

  // -- what the preview pane shows -----------------------------------------
  const serverDraftReady = dirty && shownRender?._for === draftKey && !draftTooLong;
  const useClientDraft = dirty && (draftTooLong || (!!renderErr && !serverDraftReady));
  const clientView = useClientDraft && draft ? renderDraft(draft, values) : null;
  const who = shownRender?.recipient
    ? (shownRender.recipient.email || shownRender.recipient.name || previewFor)
    : 'sample values';

  // -- render -------------------------------------------------------------
  if (res.loading && !list.length) return <Loading />;

  return (
    <div className="flex flex-col gap-4">
      <Section title={title || 'Templates (editable) — every customer e-mail, no deploy needed'}
        right={<div className="flex items-center gap-2">
          <span className="text-xs text-grayscale-1100">{list.length} templates · {nEdited} edited</span>
          <Btn small onClick={res.reload}>Refresh</Btn>
        </div>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Pick a template, edit it, save: the next e-mail of that kind uses your version. A field left blank
          uses the built-in value. Reset deletes your version and the built-in copy comes back. Placeholders
          look like {'{first_name}'}; click one below the body to insert it where the cursor is.
        </p>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Saved templates (the ones made in Campaigns, MEMAC or by hand) are edited under Email Center → Saved templates.
        </p>
        {res.error && <Msg kind="error">{res.error}</Msg>}
        <DefaultFallbacks onSaved={() => setRenderTick(t => t + 1)} />

        <div className="grid gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
          {/* ---- list ---- */}
          <div className="flex flex-col gap-2 min-w-0">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Find a template…" className={inputCls} />
            <div className="flex flex-col gap-3 max-h-[75vh] overflow-y-auto pr-1">
              {groups.map(g => (
                <div key={g.group} className="flex flex-col gap-1">
                  <div className="text-[11px] uppercase tracking-wide text-grayscale-1100 px-1">{g.group}</div>
                  {g.items.map(t => {
                    const unsaved = !!drafts[t.key] && !same(drafts[t.key], t.current);
                    return (
                      <button key={t.key} type="button" onClick={() => { setSel(t.key); setMsg(null); }}
                        className={`text-left rounded-lg border px-3 py-2 flex flex-col gap-0.5 ${
                          sel === t.key ? 'border-primary-500 bg-primary-50' : 'border-grayscale-300 bg-white hover:bg-grayscale-100'}`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium text-grayscale-1300">{t.name}</span>
                          {t.edited && <Badge>edited</Badge>}
                          {unsaved && <Badge tone="amber">unsaved</Badge>}
                          {t.orphan && <Badge tone="red">no longer used</Badge>}
                        </div>
                        {t.trigger && <div className="text-[11px] text-grayscale-1200">{t.trigger}</div>}
                        {t.edited && (
                          <div className="text-[11px] text-grayscale-1100">
                            {t.editedFields.length ? `Changed: ${t.editedFields.map(f => FIELD_LABEL[f as keyof Draft] || f).join(', ')}. ` : ''}
                            {t.updated_at ? `${fmtDate(t.updated_at)}${t.updated_by ? ` by ${t.updated_by}` : ''}` : ''}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
              {!shown.length && <div className="text-xs text-grayscale-1100 py-3 text-center">No template matches.</div>}
            </div>
          </div>

          {/* ---- editor + preview ---- */}
          {tpl && draft ? (
            <div className="grid gap-4 xl:grid-cols-2 min-w-0">
              <div className="flex flex-col gap-3 min-w-0">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-base font-bold text-grayscale-1300 font-grotesk">{tpl.name}</span>
                    <code className="text-[11px] text-grayscale-1100">{tpl.key}</code>
                    {tpl.edited ? <Badge>edited</Badge> : <Badge tone="grey">built-in copy</Badge>}
                    {dirty && <Badge tone="amber">unsaved changes</Badge>}
                    {!tpl.sendHtml && <Badge tone="grey">plain text unless it has a button</Badge>}
                  </div>
                  {tpl.trigger && <div className="text-xs text-grayscale-1200"><b>When:</b> {tpl.trigger}</div>}
                  {tpl.description && <div className="text-xs text-grayscale-1100">{tpl.description}</div>}
                  {tpl.fn && <div className="text-[11px] text-grayscale-1100">Sent by <code>{tpl.fn}</code></div>}
                  {tpl.edited && tpl.updated_at && (
                    <div className="text-[11px] text-grayscale-1100">
                      Last edited {fmtDate(tpl.updated_at)}{tpl.updated_by ? ` by ${tpl.updated_by}` : ''}
                    </div>
                  )}
                </div>

                <FieldRow label="Subject" changed={draft.subject !== tpl.defaults.subject}>
                  <input value={draft.subject} onFocus={track('subject')} placeholder={tpl.defaults.subject}
                    onChange={e => setDraft({ subject: e.target.value })} className={inputCls} />
                </FieldRow>
                <div className="grid sm:grid-cols-3 gap-2">
                  <FieldRow label="From name" changed={draft.from_name !== tpl.defaults.from_name}>
                    <input value={draft.from_name} onFocus={track('from_name')}
                      placeholder={tpl.defaults.from_name || '(none)'}
                      onChange={e => setDraft({ from_name: e.target.value })} className={inputCls} />
                  </FieldRow>
                  <FieldRow label="From email" changed={draft.from_email !== tpl.defaults.from_email}>
                    <input value={draft.from_email} placeholder={tpl.defaults.from_email || `name${domain}`}
                      onChange={e => setDraft({ from_email: e.target.value })} className={inputCls} />
                  </FieldRow>
                  <FieldRow label="Reply-To" changed={draft.reply_to !== tpl.defaults.reply_to}>
                    <input value={draft.reply_to} placeholder={tpl.defaults.reply_to || '(the From address)'}
                      onChange={e => setDraft({ reply_to: e.target.value })} className={inputCls} />
                  </FieldRow>
                </div>
                <FieldRow label="Body — plain text; a blank line starts a paragraph, lines starting “• ” or “- ” make a list"
                  changed={draft.body !== tpl.defaults.body}>
                  <textarea ref={bodyRef} value={draft.body} rows={18} onFocus={track('body')}
                    onChange={e => setDraft({ body: e.target.value })}
                    className="px-3 py-2 rounded-md border-[0.5px] border-primary-300 bg-white text-xs font-mono text-grayscale-1300 leading-relaxed" />
                </FieldRow>

                {chips.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs text-grayscale-1100">Placeholders — click to insert at the cursor</span>
                    <div className="flex flex-wrap gap-1.5">
                      {chips.map(p => {
                        const eg = values?.[p.name] ?? p.sample;
                        return (
                          <button key={p.name} type="button"
                            onMouseDown={e => e.preventDefault() /* keep the caret where it is */}
                            onClick={() => insert(p.name)}
                            title={[p.meaning, eg !== undefined && eg !== '' ? `e.g. ${eg}` : ''].filter(Boolean).join(' — ') || 'Insert'}
                            className="font-mono text-[11px] px-2 py-0.5 rounded-full border border-primary-300 bg-primary-50 text-grayscale-1300 hover:bg-primary-100">
                            {`{${p.name}}`}
                          </button>
                        );
                      })}
                    </div>
                    {chips.some(p => p.meaning) && (
                      <details className="text-[11px] text-grayscale-1100">
                        <summary className="cursor-pointer">What each placeholder means</summary>
                        <ul className="mt-1 flex flex-col gap-0.5">
                          {chips.filter(p => p.meaning).map(p => {
                            const eg = values?.[p.name] ?? p.sample;
                            return (
                              <li key={p.name}><code>{`{${p.name}}`}</code> — {p.meaning}
                                {eg !== undefined && eg !== '' ? <span> (e.g. {String(eg)})</span> : null}
                              </li>
                            );
                          })}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-grayscale-1100">
                      Buttons — up to {maxCtas}; shown above the sign-off, and as “Label: link” lines in the plain-text part
                      {JSON.stringify(draft.ctas) !== JSON.stringify(tpl.defaults.ctas) && (
                        <span className="text-amber-700"> · differs from default</span>)}
                    </span>
                    <Btn small disabled={draft.ctas.length >= maxCtas}
                      onClick={() => setDraft({ ctas: [...draft.ctas, { label: '', url: '' }] })}>
                      + Add button
                    </Btn>
                  </div>
                  {draft.ctas.map((c, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <input value={c.label} placeholder="Label, e.g. Upgrade to {plan_name}"
                        onFocus={track(`cta_label_${i}`)}
                        onChange={e => setField(`cta_label_${i}`, e.target.value)}
                        className={`${inputCls} !w-2/5`} />
                      <input value={c.url} placeholder="https://… · mailto:… · {upgrade_url}"
                        onFocus={track(`cta_url_${i}`)}
                        onChange={e => setField(`cta_url_${i}`, e.target.value)}
                        className={`${inputCls} font-mono !text-xs`} />
                      <button type="button" title="Remove this button"
                        onClick={() => setDraft({ ctas: draft.ctas.filter((_, j) => j !== i) })}
                        className="w-7 h-7 shrink-0 rounded-full hover:bg-grayscale-200 text-grayscale-1100">×</button>
                    </div>
                  ))}
                  {!draft.ctas.length && <div className="text-[11px] text-grayscale-1100">No buttons.</div>}
                </div>

                <FallbacksPanel names={fbNames} value={draft.fallbacks}
                  onChange={fb => setDraft({ fallbacks: fb })} unsetLabel="the default fallbacks"
                  title="When someone has no value (this e-mail only)" />

                {issues.errors.length > 0 && (
                  <Msg kind="error">{issues.errors.map((e, i) => <div key={i}>{e}</div>)}</Msg>
                )}
                {issues.warnings.length > 0 && (
                  <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
                    {issues.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </div>
                )}

                <div className="flex flex-wrap gap-2 items-center border-t border-grayscale-200 pt-3">
                  <Btn kind="primary" onClick={save}
                    disabled={!dirty || busy !== '' || issues.errors.length > 0}>
                    {busy === 'save' ? 'Saving…' : 'Save'}
                  </Btn>
                  {dirty && <Btn onClick={() => dropDraft(tpl.key)} disabled={busy !== ''}>Discard changes</Btn>}
                  <Btn onClick={() => setDraft(tpl.defaults)} disabled={busy !== '' || same(draft, tpl.defaults)}
                    title="Put the built-in copy in the editor without saving">
                    Load default into editor
                  </Btn>
                  <Btn kind="danger" onClick={() => setConfirm('reset')} disabled={!tpl.edited || busy !== ''}
                    title={tpl.edited ? 'Delete the saved edit' : 'Nothing to reset — this is the built-in copy'}>
                    {busy === 'reset' ? 'Resetting…' : 'Reset to default'}
                  </Btn>
                  <Btn onClick={() => setConfirm('test')} disabled={busy !== '' || issues.errors.length > 0}
                    title="Mail what is in the editor to your own admin address">
                    {busy === 'test' ? 'Sending…' : 'Send test to me'}
                  </Btn>
                </div>
                {dirty && <ChangedFields a={draft} b={tpl.current} />}
                {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
              </div>

              {/* ---- preview ---- */}
              <div className="flex flex-col gap-2 min-w-0">
                <div className="flex items-end gap-2 flex-wrap">
                  <Labeled label="Preview as">
                    <input value={previewEmail} onChange={e => setPreviewEmail(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') setPreviewFor(previewEmail.trim()); }}
                      placeholder="a user's e-mail (blank = sample values)"
                      className={`${inputCls} !w-64`} />
                  </Labeled>
                  <Btn small onClick={() => setPreviewFor(previewEmail.trim())}>Use this user</Btn>
                  {previewFor && (
                    <Btn small onClick={() => { setPreviewEmail(''); setPreviewFor(''); }}>Sample values</Btn>
                  )}
                </div>
                {shownRender?.user_not_found && (
                  <div className="text-xs text-amber-800">No user with that e-mail — showing sample values.</div>
                )}
                {renderErr && <Msg kind="error">Preview failed: {renderErr}</Msg>}
                {shownRender && <VaryingNotices varying={shownRender.varying} />}
                {clientView ? (
                  <EmailPreview subject={clientView.subject} html={clientView.html} text={clientView.text}
                    from={fmtFrom(draft.from_name || tpl.defaults.from_name, draft.from_email || tpl.defaults.from_email)}
                    replyTo={draft.reply_to || tpl.defaults.reply_to}
                    plainOnly={!tpl.sendHtml && !draft.ctas.some(c => c.label.trim() && c.url.trim())}
                    banner={
                      <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
                        {draftTooLong ? 'This draft is too long to preview on the server' : 'The server preview failed'},
                        {' '}so this is an approximation rendered in your browser (buttons are shown at the end, placeholders
                        without a value are highlighted). Save, then preview, to see the exact e-mail.
                      </div>} />
                ) : shownRender ? (
                  <EmailPreview subject={String(shownRender.subject ?? '')} html={String(shownRender.html ?? '')}
                    text={shownRender.text ?? undefined}
                    from={fmtFrom(shownRender.from_name, shownRender.from_email)}
                    replyTo={shownRender.reply_to_email
                      ? fmtFrom(shownRender.reply_to_name, shownRender.reply_to_email) : shownRender.reply_to}
                    to={shownRender.recipient?.email}
                    plainOnly={shownRender.send_html === false}
                    banner={dirty ? (
                      <div className="text-xs rounded-lg px-3 py-2 border text-amber-800 bg-amber-50 border-amber-200">
                        {serverDraftReady && !loadingPreview
                          ? <>Unsaved draft — rendered by the server exactly as it would go out, with {who}. Nothing is saved until you press Save.</>
                          : <>Updating the preview of your draft…</>}
                      </div>
                    ) : (
                      <div className="text-xs text-grayscale-1100">
                        The saved version, rendered by the server with {who}{loadingPreview ? ' — refreshing…' : ''}.
                      </div>
                    )} />
                ) : loadingPreview ? <Loading label="Rendering…" /> : null}
              </div>
            </div>
          ) : (
            <div className="text-sm text-grayscale-1100 py-6 text-center">
              {list.length ? 'Pick a template.' : 'No editable templates were returned.'}
            </div>
          )}
        </div>
      </Section>

      {confirm === 'reset' && tpl && (
        <Modal title={`Reset "${tpl.name}"?`} onClose={() => setConfirm('')}
          footer={<>
            <Btn onClick={() => setConfirm('')}>Cancel</Btn>
            <Btn kind="danger" onClick={reset}>Reset to default</Btn>
          </>}>
          <p>
            This deletes the saved edit{tpl.updated_by ? ` (last saved by ${tpl.updated_by})` : ''} and the
            built-in copy is used from the next send. Subject, sender, body and buttons all go back.
          </p>
          {dirty && <p className="text-amber-700">Your unsaved changes in the editor are discarded too.</p>}
        </Modal>
      )}
      {confirm === 'test' && tpl && (
        <Modal title="Send a test to yourself?" onClose={() => setConfirm('')}
          footer={<>
            <Btn onClick={() => setConfirm('')}>Cancel</Btn>
            <Btn kind="primary" onClick={sendTest}>Send test</Btn>
          </>}>
          <p>
            Sends <b>{tpl.name}</b> to <b>your own admin address</b> only, through SendGrid, with “[Test]” in
            front of the subject, so you can check it in a real inbox. Nobody else receives it and it is not
            counted as a customer send.
          </p>
          <p className="text-xs text-grayscale-1100">
            {dirty ? 'It uses what is in the editor now, including your unsaved changes — sending a test does not save them.'
              : 'It uses the saved version.'}
          </p>
        </Modal>
      )}
    </div>
  );
};

const FieldRow: React.FC<{ label: string; changed?: boolean; children: React.ReactNode }> =
  ({ label, changed, children }) => (
  <label className="flex flex-col gap-1 text-xs text-grayscale-1100 min-w-0">
    <span>
      {label}
      {changed && <span className="text-amber-700" title="Differs from the built-in copy"> · differs from default</span>}
    </span>
    {children}
  </label>
);

/** Which fields the unsaved draft changes, so Save is never a surprise. */
const ChangedFields: React.FC<{ a: Draft; b: Draft }> = ({ a, b }) => {
  const na = norm(a); const nb = norm(b);
  const keys = (Object.keys(FIELD_LABEL) as (keyof Draft)[])
    .filter(k => JSON.stringify(na[k]) !== JSON.stringify(nb[k]));
  if (!keys.length) return null;
  return (
    <div className="text-[11px] text-grayscale-1100">Unsaved: {keys.map(k => FIELD_LABEL[k]).join(', ')}</div>
  );
};

export default EditableTemplates;
